/**
 * FP3-043 Phase C4 — Archive / Exit Student Modal.
 *
 * Used for both single-row archive and bulk-archive flows. The caller
 * passes `studentIds` (one or many) plus a label hint; the modal
 * collects an exit reason + optional note and POSTs to the right
 * backend endpoint.
 *
 * Permissions are enforced server-side: super_admin can hit any row,
 * org_admin/admin only their own org's rows, provider/assistant/eval
 * are 403. The modal does not show for providers (parent controls
 * visibility via role check).
 *
 * No hard delete. Sets is_archived=True + status="archived" +
 * exit_reason + exit_note + exited_at + exited_by on the backend.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local fallback if the server endpoint is unreachable — keeps the
// dropdown usable.
const FALLBACK_REASONS = [
  { key: "graduated", label: "Graduated" },
  { key: "withdrew", label: "Withdrew" },
  { key: "parent_revoked_services", label: "Parent revoked services" },
  { key: "moved_transferred", label: "Moved / transferred" },
  { key: "no_longer_eligible", label: "No longer eligible" },
  { key: "duplicate_or_test_record", label: "Duplicate or test record" },
  { key: "other", label: "Other" },
];

export default function ArchiveStudentsModal({
  open,
  onOpenChange,
  studentIds,
  onArchived,
}) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState(FALLBACK_REASONS);

  const isBulk = (studentIds || []).length > 1;
  const count = (studentIds || []).length;

  useEffect(() => {
    if (!open) return;
    setReason("");
    setNote("");
    axios
      .get(`${API_URL}/admin/students/exit-reasons`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setReasons(r.data);
      })
      .catch(() => {
        /* keep fallback */
      });
  }, [open]);

  const submit = async () => {
    if (!reason) {
      toast.error("Pick an exit reason");
      return;
    }
    if (!studentIds?.length) return;
    setSubmitting(true);
    try {
      if (isBulk) {
        const r = await axios.post(
          `${API_URL}/admin/students/bulk-archive`,
          { student_ids: studentIds, exit_reason: reason, exit_note: note },
        );
        const archived = r?.data?.archived_count || 0;
        const skipped = r?.data?.skipped_count || 0;
        toast.success(
          `Archived ${archived} student${archived === 1 ? "" : "s"}` +
            (skipped ? ` · ${skipped} skipped (permission / not-found)` : "")
        );
      } else {
        await axios.post(
          `${API_URL}/admin/students/${studentIds[0]}/archive`,
          { exit_reason: reason, exit_note: note },
        );
        toast.success("Student archived");
      }
      if (typeof onArchived === "function") onArchived();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not archive — check your permissions and try again"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[460px]"
        data-testid="archive-students-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {isBulk
              ? `Archive ${count} students organization-wide`
              : "Archive student organization-wide"}
          </DialogTitle>
          <DialogDescription>
            This is an organization-wide action: it removes{" "}
            {isBulk ? "the selected students" : "the student"} from active
            workflows for <strong>every provider on the team</strong>.
            To end only your own service (e.g. graduating a student from
            speech while OT continues), use “Archive from my caseload” on
            the student page instead. History (goals, sessions, notes) is
            preserved and accessible by toggling "Include archived". No
            record is deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="archive-reason" className="text-[12.5px] font-semibold">
              Exit reason
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger
                id="archive-reason"
                data-testid="archive-reason-trigger"
                className="h-9 text-[13px]"
              >
                <SelectValue placeholder="Choose a reason..." />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem
                    key={r.key}
                    value={r.key}
                    data-testid={`archive-reason-${r.key}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="archive-note"
              className="text-[12.5px] font-semibold"
            >
              Note (optional)
            </Label>
            <Textarea
              id="archive-note"
              data-testid="archive-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context for the team (visible in audit history)..."
              maxLength={500}
              className="text-[13px]"
            />
            <div className="text-[10.5px] text-[#9AA0A6] text-right">
              {note.length}/500
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="archive-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !reason}
            className="bg-[#7A3A2C] hover:bg-[#62301F] text-white"
            data-testid="archive-confirm-btn"
          >
            {submitting
              ? "Archiving..."
              : isBulk
              ? `Archive ${count} students`
              : "Archive / Exit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
